import { codesApi as api } from './base-api';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV1StatsBySlugScans: build.query<
      GetV1StatsBySlugScansApiResponse,
      GetV1StatsBySlugScansApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/stats/${queryArg.slug}/scans`,
        params: {
          days: queryArg.days,
          scan: queryArg.scan,
        },
      }),
    }),
    getV1Health: build.query<GetV1HealthApiResponse, GetV1HealthApiArg>({
      query: () => ({ url: `/v1/health` }),
    }),
    getV2QrCodes: build.query<GetV2QrCodesApiResponse, GetV2QrCodesApiArg>({
      query: (queryArg) => ({
        url: `/v2/qr/codes`,
        params: {
          username: queryArg.username,
          locationCountry: queryArg.locationCountry,
          locationSite: queryArg.locationSite,
          communicationPurpose: queryArg.communicationPurpose,
          subcategory: queryArg.subcategory,
          storeComponent: queryArg.storeComponent,
          storeDepartment: queryArg.storeDepartment,
          carrierType: queryArg.carrierType,
          carrierSize: queryArg.carrierSize,
          appClientId: queryArg.appClientId,
        },
      }),
    }),
    postV2QrCodes: build.mutation<
      PostV2QrCodesApiResponse,
      PostV2QrCodesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/qr/codes`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    getV2QrCodesBySlug: build.query<
      GetV2QrCodesBySlugApiResponse,
      GetV2QrCodesBySlugApiArg
    >({
      query: (queryArg) => ({ url: `/v2/qr/codes/${queryArg.slug}` }),
    }),
    putV2QrCodesBySlug: build.mutation<
      PutV2QrCodesBySlugApiResponse,
      PutV2QrCodesBySlugApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/qr/codes/${queryArg.slug}`,
        method: 'PUT',
        body: queryArg.updateCodeV2,
      }),
    }),
    deleteV2QrCodesBySlug: build.mutation<
      DeleteV2QrCodesBySlugApiResponse,
      DeleteV2QrCodesBySlugApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/qr/codes/${queryArg.slug}`,
        method: 'DELETE',
      }),
    }),
    getV2Sites: build.query<GetV2SitesApiResponse, GetV2SitesApiArg>({
      query: () => ({ url: `/v2/sites` }),
    }),
    postV2QrCodesBySlugRestore: build.mutation<
      PostV2QrCodesBySlugRestoreApiResponse,
      PostV2QrCodesBySlugRestoreApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/qr/codes/${queryArg.slug}/restore`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as api };
export type GetV1StatsBySlugScansApiResponse =
  /** status 200 Fetched code stats. */ CodeScanStats;
export type GetV1StatsBySlugScansApiArg = {
  /** 6 digit slug, unique for each code. */
  slug: string;
  /** Number of days back to fetch statistics for. */
  days?: number;
  /** If the QR code has been scanned through camera or direct link access. */
  scan?: string;
};
export type GetV1HealthApiResponse = unknown;
export type GetV1HealthApiArg = void;
export type GetV2QrCodesApiResponse =
  /** status 200 Return all codes matching the filters. */ CodeListV2;
export type GetV2QrCodesApiArg = {
  username?: string;
  locationCountry?: string[];
  locationSite?: string[];
  communicationPurpose?: string[];
  subcategory?: string[];
  storeComponent?: string[];
  storeDepartment?: string[];
  carrierType?: string[];
  carrierSize?: string[];
  appClientId?: string;
};
export type PostV2QrCodesApiResponse = /** status 201 Created. */ CodeV2;
export type PostV2QrCodesApiArg = {
  body: CodeCommonPropsV2;
};
export type GetV2QrCodesBySlugApiResponse =
  /** status 200 Return code. */ CodeV2;
export type GetV2QrCodesBySlugApiArg = {
  /** 6 digit slug, unique for each code. */
  slug: string;
};
export type PutV2QrCodesBySlugApiResponse =
  /** status 200 Update completed. */ CodeV2;
export type PutV2QrCodesBySlugApiArg = {
  /** 6 digit slug, unique for each code. */
  slug: string;
  updateCodeV2: UpdateCodeV2;
};
export type DeleteV2QrCodesBySlugApiResponse = unknown;
export type DeleteV2QrCodesBySlugApiArg = {
  /** 6 digit slug, unique for each code. */
  slug: string;
};
export type GetV2SitesApiResponse =
  /** status 200 Location sites that contain codes within hour interval. */ ListLocations;
export type GetV2SitesApiArg = void;
export type PostV2QrCodesBySlugRestoreApiResponse =
  /** status 200 Return code. */ CodeV2;
export type PostV2QrCodesBySlugRestoreApiArg = {
  /** 6 digit slug, unique for each code. */
  slug: string;
};
export type CodeScanStats = {
  [key: string]: string[];
};
export type OpenApiValidatorError = {
  /** Error location in OpenAPI specifications. */
  path: string;
  /** Error message for the specific validation. */
  message: string;
  /** OpenAPI validator errorCode which defines the rule this error is based upon. */
  errorCode: string;
};
export type OpenApiValidatorCompleteError = {
  /** Status code of error response */
  status: string;
  /** Overarching error message for validation. */
  message: string;
  errors: OpenApiValidatorError[];
};
export type Unauthorized = string;
export type InternalServerError = {
  /** Internal server error. */
  error: string;
};
export type CodePermissions = {
  /** Type of permission (user or location). */
  type: 'user' | 'location';
  /** Value of the permission (email for user, cdsLocation for location). */
  value: string;
};
export type CodeCommonPropsV2 = {
  /** Code name */
  codeName: string;
  /** URL at which the slug will point. */
  targetUrl: string;
  /** Name of the icon to be used for the code. */
  icon?: string;
  /** Code creator data */
  user: {
    /** Email of the code creator. */
    email: string;
    /** Country of the code creator. */
    country: string;
    /** CDS location name of the code creator. */
    location: string;
    /** Role of the code creator. */
    role?: string;
    /** Display name of the code creator. */
    name?: string;
  };
  /** The particular group of people that this QR code is intended to be readed by. */
  targetGroup?: string;
  /** Communication purpose product, book, tools, feedback (only for customer QR). */
  communicationPurpose?: string;
  /** Subcategory of the communication purpose. */
  subcategory?: string;
  /** Code location data. */
  location?: {
    /** Country of the QR code. */
    country?: string;
    /** Full store address, including city name, area and address number where the QR code is going to be placed. */
    site?: string;
  };
  /** If true, the QR code will be used only online. */
  onlyOnlineUsage?: boolean;
  /** Code store data. */
  store?: {
    /** Specific section of the store intended for a particular function in the shopping and operating experience where the QR is going to be placed (only for customer QR). */
    component?: string[];
    /** A specific section of the store that is dedicated to a particular category of products where the QR is going to be placed (only for customer QR). */
    department?: string[];
  };
  /** Detailed information on the QR code carrier, including the formats in which it will be presented and the physical dimensions in which it will be printed. */
  carrier?: {
    /** Format in which the QR will be presented, specifying how it will be displayed. */
    type?: string[];
    /** The physical size on which the QR code is to be printed (for example":" a4, 50x70, ...). */
    size?: string[];
  };
  /** Edit permissions for the code. */
  permissions?: CodePermissions[];
};
export type CodeV2 = CodeCommonPropsV2 & {
  /** Timestamp when the code was created. */
  created: string;
  /** Number of scans of this QR code. */
  scans: number;
  /** URL slug */
  slug?: string;
  /** Short url that redirects to the targetUrl. */
  shortUrl?: string;
  /** Short url that increases the scan count and redirects to the targetUrl. */
  scanUrl?: string;
};
export type CodeListV2 = CodeV2[];
export type StatusError = {
  /** Handled error message, likely usage error. */
  message: string;
};
export type UpdateCodeV2 = {
  /** Code name */
  codeName?: string;
  /** URL at which the slug will point. */
  targetUrl?: string;
  /** Name of the icon to be used for the code. */
  icon?: string;
  /** Code creator data */
  user?: {
    /** Email of the code creator. */
    email: string;
    /** Country of the code creator. */
    country: string;
    /** CDS location name of the code creator. */
    location: string;
    /** Role of the code creator. */
    role?: string;
  };
  /** The particular group of people that this QR code is intended to be readed by. */
  targetGroup?: string;
  /** Communication purpose product, book, tools, feedback (only for customer QR). */
  communicationPurpose?: string;
  /** Subcategory of the communication purpose. */
  subcategory?: string;
  /** Code location data. */
  location?: {
    /** Country of the QR code. */
    country?: string;
    /** Full store address, including city name, area and address number where the QR code is going to be placed. */
    site?: string;
  };
  /** If true, the QR code will be used only online. */
  onlyOnlineUsage?: boolean;
  /** Code store data. */
  store?: {
    /** Specific section of the store intended for a particular function in the shopping and operating experience where the QR is going to be placed (only for customer QR). */
    component?: string[];
    /** A specific section of the store that is dedicated to a particular category of products where the QR is going to be placed (only for customer QR). */
    department?: string[];
    /** A specific area within the store that is dedicated to a particular type of product or function where the QR is going to be placed (only for coworker QR). */
    area?: string[];
  };
  /** Detailed information on the QR code carrier, including the formats in which it will be presented and the physical dimensions in which it will be printed. */
  carrier?: {
    /** Format in which the QR will be presented, specifying how it will be displayed. */
    type?: string[];
    /** The physical size on which the QR code is to be printed (for example":" a4, 50x70, ...). */
    size?: string[];
  };
};
export type Location = {
  /** Name of location (from AD, CDS) */
  name: string;
  /** Country in which location is in. */
  country: string;
};
export type ListLocations = Location[];
export const {
  useGetV1StatsBySlugScansQuery,
  useGetV1HealthQuery,
  useGetV2QrCodesQuery,
  usePostV2QrCodesMutation,
  useGetV2QrCodesBySlugQuery,
  usePutV2QrCodesBySlugMutation,
  useDeleteV2QrCodesBySlugMutation,
  useGetV2SitesQuery,
  usePostV2QrCodesBySlugRestoreMutation,
} = injectedRtkApi;
